export interface Fix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

const toFix = (p: GeolocationPosition): Fix => ({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy });

/** A fix at least this accurate (metres) is taken as is, without trying for a better one. */
export const GOOD_ENOUGH_ACCURACY_M = 40;
/** The longest a remembered fix is still trusted as a fallback. */
export const MAX_STALE_FIX_MS = 2 * 60_000;

interface Remembered extends Fix {
  at: number;
}
let lastKnown: Remembered | null = null;
const remember = (fix: Fix): Fix => {
  lastKnown = { ...fix, at: Date.now() };
  return fix;
};

/** Extra time past a request's own `timeout` before it is given up on here. */
export const POSITION_GRACE_MS = 2000;

/**
 * One position request. Some browsers (WebKit, notably after a PWA comes back from the
 * background) sometimes never call either callback, even with a `timeout`, so the request
 * is also given up on here, as a timeout, shortly after its own `timeout` passes. Without
 * that a hung request would hang every caller waiting on it.
 */
function getPosition(options: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(Object.assign(new Error('Geolocation unsupported'), { code: 2 }));
      return;
    }
    const timer =
      options.timeout != null
        ? setTimeout(() => reject(Object.assign(new Error('Position request timed out'), { code: 3 })), options.timeout + POSITION_GRACE_MS)
        : undefined;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        resolve(p);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
      options,
    );
  });
}

/** The most accurate of `fixes`, ignoring missing ones; null when there are none. */
export function bestFix(fixes: (Fix | null | undefined)[]): Fix | null {
  let best: Fix | null = null;
  for (const f of fixes) if (f && (best == null || f.accuracy < best.accuracy)) best = f;
  return best;
}

async function attempt(options: PositionOptions): Promise<{ fix: Fix | null; error?: unknown }> {
  try {
    return { fix: toFix(await getPosition(options)) };
  } catch (error) {
    return { fix: null, error };
  }
}

/**
 * The best position available for a scan or refresh, tuned for indoor use, like the
 * Flutter client. A high-accuracy request is tried first; unless it is already good, a
 * balanced one (Wi-Fi and cell data) is tried too and the more accurate fix wins. A
 * recent remembered fix is the final fallback. Throws only when nothing usable exists
 * (a denied permission is thrown straight away).
 */
export async function currentPosition(): Promise<Fix> {
  const precise = await attempt({ enableHighAccuracy: true, timeout: 6000, maximumAge: 0 });
  if (precise.fix && precise.fix.accuracy <= GOOD_ENOUGH_ACCURACY_M) return remember(precise.fix);
  if (precise.error && (precise.error as GeolocationPositionError).code === 1) throw precise.error;

  const balanced = await attempt({ enableHighAccuracy: false, timeout: 5000, maximumAge: 30_000 });
  const recent = lastKnown && Date.now() - lastKnown.at <= MAX_STALE_FIX_MS ? lastKnown : null;
  const best = bestFix([precise.fix, balanced.fix, recent]);
  if (best) return recent && best === recent ? best : remember(best);
  throw precise.error ?? balanced.error ?? new Error('No position fix available');
}

/** Resolve the permission state to a coarse string without prompting; `null` if unknown. */
export async function queryPermission(): Promise<PermissionState | null> {
  try {
    if (!navigator.permissions?.query) return null;
    return (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch {
    return null;
  }
}

/** Prompt (if the browser still will) by asking for a position. */
export async function requestPosition(): Promise<'granted' | 'denied' | 'unavailable'> {
  try {
    remember(toFix(await getPosition({ enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 })));
    return 'granted';
  } catch (e) {
    const code = (e as GeolocationPositionError).code;
    if (code === 1) return 'denied';
    if (code === 3) return 'granted'; // permission given, no fix yet
    return 'unavailable';
  }
}

export function onPermissionChange(cb: () => void): () => void {
  let status: PermissionStatus | undefined;
  let disposed = false;
  navigator.permissions
    ?.query({ name: 'geolocation' })
    .then((s) => {
      if (disposed) return;
      status = s;
      s.addEventListener('change', cb);
    })
    .catch(() => undefined);
  return () => {
    disposed = true;
    status?.removeEventListener('change', cb);
  };
}
